import { useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Package, Loader2, Search, Truck, ScanBarcode } from "lucide-react";
import { CargusSetupBanner } from "@/components/CargusSetupBanner";

// order = display order for filter chips + stat cards
const STATES = [
  { id: "livrat", label: "Livrat", color: "#34C759" },
  { id: "livrare", label: "Spre destinatar", color: "#4DA3FF" },
  { id: "tranzit", label: "În tranzit", color: "#AF7AFF" },
  { id: "depozit", label: "În depozit", color: "#FF9F0A" },
  { id: "preluat", label: "Preluat", color: "#32D6C0" },
  { id: "retur", label: "Retur", color: "#FF3B30" },
  { id: "netrimis", label: "Netrimisă", color: "#8E8E93" },
  { id: "anulat", label: "Anulat", color: "#8E8E93" },
];

function Stat({ label, value, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`as-stat-${label}`}
      className={`text-left border rounded-sm bg-card p-4 transition-colors ${
        active ? "border-primary" : "border-border hover:border-muted-foreground"
      }`}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground truncate">{label}</div>
      <div className="mt-2 text-2xl font-mono font-semibold tracking-tight" style={{ color }}>
        {value}
      </div>
    </button>
  );
}

export default function AwbStatus() {
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");

  const counts = useMemo(() => {
    const a = result?.awbs || [];
    const c = {};
    STATES.forEach((s) => (c[s.id] = 0));
    a.forEach((x) => (c[x.delivery_state] = (c[x.delivery_state] || 0) + 1));
    return c;
  }, [result]);

  const filtered = useMemo(() => {
    let a = result?.awbs || [];
    if (filter !== "all") a = a.filter((x) => x.delivery_state === filter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      a = a.filter(
        (x) =>
          x.awb.includes(query.trim()) ||
          (x.receiver || "").toLowerCase().includes(q) ||
          (x.locality || "").toLowerCase().includes(q)
      );
    }
    return a;
  }, [result, filter, query]);

  const run = async () => {
    if (!fromDate || !toDate) {
      toast.error("Completează intervalul de date (de la / până la).");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("from_date", fromDate);
      fd.append("to_date", toDate);
      const { data } = await api.post("/verify/awb-status", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
      });
      setResult(data);
      setFilter("all");
      toast.success(`${data.total} AWB-uri găsite în interval`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  const field =
    "mt-1 w-full bg-card border border-border rounded-sm px-2.5 py-2 text-sm font-mono outline-none focus:border-primary";

  return (
    <Layout>
      <div className="p-8 max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2 mb-1">
          <Truck size={20} />
          <h1 className="text-3xl font-semibold tracking-tight">Status AWB-uri după dată</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1 mb-8">
          Fără fișiere. Pui doar intervalul, iar Cargus îți arată toate AWB-urile expediate și statusul
          lor de livrare: livrat, pe drum, în depozit, în tranzit, retur, netrimisă.
        </p>

        <CargusSetupBanner />

        <div className="border border-border rounded-sm bg-card p-5 mb-8 max-w-2xl">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">De la data</label>
              <input
                data-testid="as-from-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={field}
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground uppercase tracking-wider">Până la data</label>
              <input
                data-testid="as-to-date"
                type="text"
                inputMode="numeric"
                placeholder="zz.ll.aaaa"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={field}
              />
            </div>
          </div>
          <p className="text-[10px] text-muted-foreground font-mono mt-2 mb-4">
            Scrie liber: ex. 01.09.2026 (zz.ll.aaaa). Intervalele mari (o lună) pot dura ~60-90 sec.
          </p>

          <button
            data-testid="as-verify-btn"
            disabled={loading}
            onClick={run}
            className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-sm px-3 py-2.5 text-sm font-medium hover:bg-white/90 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <ScanBarcode size={16} />}
            {loading ? "Se verifică în Cargus..." : "Verifică statusul AWB-urilor"}
          </button>
        </div>

        {result && (
          <div className="animate-fade-up">
            <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-9 gap-3 mb-6">
              <Stat
                label="Total"
                value={result.total}
                color="#FFFFFF"
                active={filter === "all"}
                onClick={() => setFilter("all")}
              />
              {STATES.map((s) => (
                <Stat
                  key={s.id}
                  label={s.label}
                  value={counts[s.id] || 0}
                  color={s.color}
                  active={filter === s.id}
                  onClick={() => setFilter(counts[s.id] ? s.id : filter)}
                />
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="flex flex-wrap gap-1.5">
                <button
                  data-testid="as-filter-all"
                  onClick={() => setFilter("all")}
                  className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                    filter === "all"
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                  }`}
                >
                  Toate ({result.total})
                </button>
                {STATES.filter((s) => counts[s.id]).map((s) => (
                  <button
                    key={s.id}
                    data-testid={`as-filter-${s.id}`}
                    onClick={() => setFilter(s.id)}
                    className={`px-3 py-1.5 rounded-sm text-xs font-mono border transition-colors ${
                      filter === s.id
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
                    }`}
                  >
                    {s.label} ({counts[s.id]})
                  </button>
                ))}
              </div>
              <div className="relative ml-auto">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  data-testid="as-search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Caută AWB, nume sau oraș..."
                  className="bg-card border border-border rounded-sm pl-9 pr-3 py-1.5 text-sm font-mono outline-none focus:border-primary w-72"
                />
              </div>
            </div>

            <div className="border border-border rounded-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm font-mono">
                  <thead className="bg-[#0c0c0e] sticky top-0">
                    <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                      <th className="px-4 py-3 font-medium">AWB</th>
                      <th className="px-4 py-3 font-medium">Destinatar</th>
                      <th className="px-4 py-3 font-medium">Localitate</th>
                      <th className="px-4 py-3 font-medium">Data expediere</th>
                      <th className="px-4 py-3 font-medium">Status</th>
                      <th className="px-4 py-3 font-medium">Ultimul eveniment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((a) => (
                      <tr
                        key={a.awb}
                        data-testid={`as-row-${a.awb}`}
                        className="border-t border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium">{a.awb}</td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[200px] truncate" title={a.receiver}>
                          {a.receiver || "—"}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{a.locality || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{a.date || "—"}</td>
                        <td className="px-4 py-3">
                          <StatusBadge type={a.delivery_state}>{a.state_label}</StatusBadge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground max-w-[320px] truncate" title={a.last_status}>
                          {a.last_status || "—"}
                        </td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                          <Package size={20} className="mx-auto mb-2 opacity-50" />
                          Niciun AWB pentru acest filtru.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
